import { useEffect, useState } from "react";
import { apiPatch, apiPost, tryApiGet } from "../api/client";
import { useAppData } from "../state/AppData";
import { colorFor, STATUS } from '../lib/definitions/palette';
import { automationKindInfo, type AutomationKind } from '../lib/definitions/hazards';
import { type Protocol } from '../lib/definitions/protocols';
import { relativeTime } from "../lib/format";
import type { Automation, AutomationPreviewResponse, AutomationsResponse } from "../api/types";

// Alerts > Broadcasts. Modeled on MeshMonitor's menu for automation —
// no user should need to know the code or anything else to set
// up automations and alerts and auto replies and timed sends. The
// generic rule builder this replaced was rejected outright for being a
// form, not a product — this is
// purpose-named guided sections instead, one per real MeshMonitor-style
// automation kind, built against a real backend contract:
//
//   GET   /api/automations?protocol=&kind=
//   PATCH /api/automations/{id}   { enabled, config }
//   POST  /api/automations/{id}/preview -> { rendered, would_send_to, sent, trace }
//
// Not live on this backend as of this file's own writing — confirmed via
// OpenAPI, no /api/automations path yet (the coordinator is briefing it).
// Degrades the same honest way every other not-yet-shipped endpoint in
// this app does: an "unavailable" state, never a fake row.
//
// `config`'s exact field names per kind are NOT part of the given
// contract (deliberately "schema-declared", not pinned) — the fields
// rendered below are this session's own proposal, informed by reading
// MeshMonitor's real AutoAcknowledgeSection/AutoAnnounceSection/
// AutoWelcomeSection and a similar Cold-Start Grace concept elsewhere, not invented
// from nothing and not confirmed against a live backend either. Expect a
// follow-up alignment pass once the real backend lands with its own
// chosen field names.
//
// PREVIEW IS A REAL CALL, not decoration and not a pure client-side
// render: it runs the automation through the real dispatcher, which can
// only reach the simulated transport while live transmit is off — the
// coordinator's own reasoning for why this is safe to build and fire now,
// unlike the earlier flat "no send button anywhere" rule. `sent` is
// rendered honestly (inert when false, loud when true) exactly like every
// other place this app shows a derived send-state, never hidden.
//
// No dropdowns: every choice here is a toggle, a number, a text field, or
// (for the future — none of the four kinds below need one yet) a tab row.
type Loadable<T> = T | null | "unavailable";

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : {};
}

function numberField(config: Record<string, unknown>, key: string): number | "" {
  const v = config[key];
  return typeof v === "number" ? v : "";
}

function stringField(config: Record<string, unknown>, key: string): string {
  const v = config[key];
  return typeof v === "string" ? v : "";
}

function boolField(config: Record<string, unknown>, key: string): boolean {
  return config[key] === true;
}

// One row per config field this kind exposes — path, label, kind of
// input, and a plain-English explanation (no code knowledge required —
// this text is the guidance, not a tooltip).
interface FieldSpec {
  key: string;
  label: string;
  type: "number" | "text" | "textarea" | "bool";
  help: string;
}

const FIELDS_BY_KIND: Record<AutomationKind, FieldSpec[]> = {
  auto_reply: [
    { key: "regex", label: "Match pattern", type: "text", help: "Tested against incoming message text — MeshMonitor's own default is ^(test|ping)." },
    { key: "channel_allowlist", label: "Channel allowlist", type: "text", help: "Comma-separated channel names this may reply on. Empty means every channel." },
    { key: "cooldown_seconds", label: "Cooldown (seconds)", type: "number", help: "How soon the same subject may trigger another reply." },
    { key: "reply_message", label: "Reply message", type: "textarea", help: "The exact text sent back. Use Preview below to see it rendered against real current data." },
  ],
  scheduled_announcement: [
    { key: "interval_seconds", label: "Interval (seconds)", type: "number", help: "How often this announcement repeats." },
    { key: "announce_on_start", label: "Announce when this automation turns on", type: "bool", help: "Sends one immediately when enabled, rather than waiting a full interval." },
    { key: "channel", label: "Channel", type: "text", help: "Where this announcement is sent on this protocol." },
    { key: "message", label: "Message", type: "textarea", help: "The exact text sent each time." },
  ],
  welcome_node: [
    { key: "delay_seconds", label: "Delay before sending (seconds)", type: "number", help: "Waits this long after a new node is first seen, so a brief flicker isn't treated as a real new node." },
    { key: "max_hops", label: "Maximum hops", type: "number", help: "Only welcomes nodes heard within this many hops." },
    { key: "channel", label: "Channel (blank = DM the new node)", type: "text", help: "Where the welcome message is sent." },
    { key: "message", label: "Message", type: "textarea", help: "The exact text sent once, the first time a node is seen." },
  ],
  cold_start_grace: [
    { key: "grace_period_seconds", label: "Grace period (seconds)", type: "number", help: "Suppresses broadcasts for this long after the first event arrives — so starting up never floods the mesh." },
  ],
};

function FieldInput({ spec, config, onChange }: { spec: FieldSpec; config: Record<string, unknown>; onChange: (key: string, value: unknown) => void }) {
  if (spec.type === "bool") {
    return (
      <label className="settings-field" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        <input type="checkbox" checked={boolField(config, spec.key)} onChange={(e) => onChange(spec.key, e.target.checked)} />
        <span>{spec.label}</span>
        <span className="settings-field-desc">{spec.help}</span>
      </label>
    );
  }
  if (spec.type === "number") {
    return (
      <div className="settings-field">
        <span>{spec.label}</span>
        <input type="number" value={numberField(config, spec.key)} onChange={(e) => onChange(spec.key, e.target.value === "" ? "" : Number(e.target.value))} />
        <span className="settings-field-desc">{spec.help}</span>
      </div>
    );
  }
  if (spec.type === "textarea") {
    return (
      <div className="settings-field">
        <span>{spec.label}</span>
        <textarea rows={2} value={stringField(config, spec.key)} onChange={(e) => onChange(spec.key, e.target.value)} />
        <span className="settings-field-desc">{spec.help}</span>
      </div>
    );
  }
  return (
    <div className="settings-field">
      <span>{spec.label}</span>
      <input type="text" value={stringField(config, spec.key)} onChange={(e) => onChange(spec.key, e.target.value)} />
      <span className="settings-field-desc">{spec.help}</span>
    </div>
  );
}

function AutomationCard({ automation, onSaved }: { automation: Automation; onSaved: (next: Automation) => void }) {
  const { effectiveTheme } = useAppData();
  const [enabled, setEnabled] = useState(automation.enabled);
  const [config, setConfig] = useState<Record<string, unknown>>(() => ({ ...asRecord(automation.config) }));
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [preview, setPreview] = useState<AutomationPreviewResponse | "loading" | "error" | null>(null);

  const info = automationKindInfo(automation.kind);
  const fields = automation.kind in FIELDS_BY_KIND ? FIELDS_BY_KIND[automation.kind as AutomationKind] : [];

  function setField(key: string, value: unknown) {
    setConfig((prev) => ({ ...prev, [key]: value }));
    setSaveMsg(null);
  }

  async function onSave() {
    setSaving(true);
    setSaveMsg(null);
    try {
      const next = await apiPatch<Automation>(`/api/automations/${encodeURIComponent(automation.id)}`, { enabled, config });
      onSaved(next ?? { ...automation, enabled, config });
      setSaveMsg({ kind: "ok", text: "Saved." });
    } catch (e) {
      setSaveMsg({ kind: "error", text: e instanceof Error ? e.message : "Save failed." });
    } finally {
      setSaving(false);
    }
  }

  async function onPreview() {
    setPreview("loading");
    try {
      const res = await apiPost<AutomationPreviewResponse>(`/api/automations/${encodeURIComponent(automation.id)}/preview`);
      setPreview(res);
    } catch {
      setPreview("error");
    }
  }

  return (
    <div className="broadcast-rule">
      <div className="broadcast-rule-header">
        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <strong>{info.label}</strong>
        </label>
        {automation.last_fired_at && <span className="settings-field-desc">Last fired {relativeTime(automation.last_fired_at)} · {automation.fire_count} time(s)</span>}
      </div>
      <p className="settings-field-desc">{info.description}</p>

      {fields.map((spec) => (
        <FieldInput key={spec.key} spec={spec} config={config} onChange={setField} />
      ))}

      {automation.kind === "auto_reply" && (
        <div className="node-detail-note">
          Direct messages always match, regardless of the channel allowlist above — this is not a setting and cannot
          be turned off here, matching MeshMonitor's own Auto-Acknowledge behaviour.
        </div>
      )}

      <div className="settings-section-actions">
        <button type="button" className="settings-save-btn" onClick={onSave} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </button>
        <button type="button" className="settings-save-btn" onClick={onPreview}>
          Preview
        </button>
        {saveMsg && (
          <span className="settings-save-status" data-kind={saveMsg.kind}>
            {saveMsg.text}
          </span>
        )}
      </div>

      {preview === "loading" && <div className="node-detail-note">Running this automation now against real current data…</div>}
      {preview === "error" && <div className="node-detail-note">Preview didn't respond as expected.</div>}
      {preview && preview !== "loading" && preview !== "error" && (
        <div className="broadcast-preview">
          <div className="page-section-title" style={{ fontSize: 11 }}>
            Rendered — this actually ran, but only the simulated transport is reachable while live transmit is off
          </div>
          <div className="node-detail-json">{preview.rendered || "(empty message)"}</div>
          <div className="settings-field-desc">
            Would send to: {preview.would_send_to.length > 0 ? preview.would_send_to.map((w) => `${w.transport} → ${w.channel}`).join(", ") : "(nothing resolved)"}
          </div>
          <div>
            {preview.sent ? (
              <span className="badge" style={{ background: colorFor(STATUS.critical, effectiveTheme) }}>
                SENT
              </span>
            ) : (
              <span className="settings-field-desc">Not sent.</span>
            )}
          </div>
          {preview.trace.length > 0 && (
            <div className="node-list-table-wrap">
              <table className="node-list-table">
                <thead>
                  <tr>
                    <th>Rule</th>
                    <th>Result</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.trace.map((t, i) => (
                    <tr key={`${t.rule}-${i}`}>
                      <td>{t.rule}</td>
                      <td>
                        <span className="badge" style={{ background: t.passed ? colorFor(STATUS.good, effectiveTheme) : "var(--ink-muted)" }}>
                          {t.passed ? "Passed" : "Did not pass"}
                        </span>
                      </td>
                      <td>{t.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function AutomationsProtocolPanel({ protocol }: { protocol: Protocol }) {
  const [automations, setAutomations] = useState<Loadable<Automation[]>>(null);

  useEffect(() => {
    let cancelled = false;
    setAutomations(null);
    tryApiGet<AutomationsResponse>(`/api/automations?protocol=${encodeURIComponent(protocol)}`)
      .then((res) => !cancelled && setAutomations(res?.automations ?? "unavailable"))
      .catch(() => !cancelled && setAutomations("unavailable"));
    return () => {
      cancelled = true;
    };
  }, [protocol]);

  function replaceAutomation(next: Automation) {
    setAutomations((prev) => (Array.isArray(prev) ? prev.map((a) => (a.id === next.id ? next : a)) : prev));
  }

  return (
    <section className="page-section">
      <div className="page-section-title">{protocol === "meshtastic" ? "Meshtastic" : "MeshCore"}</div>
      {automations === null && <div className="node-detail-note">Loading…</div>}
      {automations === "unavailable" && (
        <div className="node-detail-note">Automations aren't available on this build yet.</div>
      )}
      {Array.isArray(automations) &&
        (automations.length === 0 ? (
          <div className="empty-state">No automations configured for this protocol yet.</div>
        ) : (
          automations.map((a) => <AutomationCard key={a.id} automation={a} onSaved={replaceAutomation} />)
        ))}
    </section>
  );
}
